import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, ChevronUp, ChevronDown } from 'lucide-react';
import { AvailableSubscriptionResource, CreateSubscriptionRequest } from '../types';
import api from '../services/api';
import LoadingSpinner from './LoadingSpinner';

interface SubscriptionFormProps {
  isOpen: boolean;
  onClose: () => void;
  subscription: AvailableSubscriptionResource | null;
  onSubmit: () => void;
}

const SubscriptionForm: React.FC<SubscriptionFormProps> = ({
  isOpen,
  onClose,
  subscription,
  onSubmit
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<CreateSubscriptionRequest>({
    name: '',
    description: '',
    maxBranch: 1,
    fnbMode: true,
    maxTransactionPerMonth: 0,
    maxTransactionPerDay: 0,
    months: 1,
    price: 0,
    annuallyDiscountPercent: 0,
    themeColor: '#3B82F6',
    isPopular: false,
    isShow: true,
    externalSubscribeLink: '',
    benefits: []
  });
  const [newBenefit, setNewBenefit] = useState('');

  useEffect(() => {
    if (subscription) {
      // Edit mode - populate form with existing data
      setFormData({
        name: subscription.name,
        description: subscription.description || '',
        maxBranch: subscription.maxBranch,
        fnbMode: subscription.fnbMode,
        maxTransactionPerMonth: subscription.maxTransactionPerMonth,
        maxTransactionPerDay: subscription.maxTransactionPerDay || 0,
        months: subscription.months,
        price: subscription.price,
        annuallyDiscountPercent: subscription.annuallyDiscountPercent || 0,
        themeColor: subscription.themeColor || '#3B82F6',
        isPopular: subscription.isPopular,
        isShow: subscription.isShow,
        externalSubscribeLink: subscription.externalSubscribeLink || '',
        benefits: subscription.benefits || []
      });
    } else {
      // Create mode - reset form
      setFormData({
        name: '',
        description: '',
        maxBranch: 1,
        fnbMode: true,
        maxTransactionPerMonth: 0,
        maxTransactionPerDay: 0,
        months: 1,
        price: 0,
        annuallyDiscountPercent: 0,
        themeColor: '#3B82F6',
        isPopular: false,
        isShow: true,
        externalSubscribeLink: '',
        benefits: []
      });
    }
    setError(null);
  }, [subscription, isOpen]);

  const handleInputChange = (field: keyof CreateSubscriptionRequest, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddBenefit = () => {
    if (newBenefit.trim()) {
      setFormData(prev => ({
        ...prev,
        benefits: [...(prev.benefits || []), newBenefit.trim()]
      }));
      setNewBenefit('');
    }
  };

  const handleRemoveBenefit = (index: number) => {
    setFormData(prev => ({
      ...prev,
      benefits: prev.benefits?.filter((_, i) => i !== index) || []
    }));
  };

  const handleMoveBenefitUp = (index: number) => {
    if (index > 0) {
      setFormData(prev => {
        const newBenefits = [...(prev.benefits || [])];
        [newBenefits[index - 1], newBenefits[index]] = [newBenefits[index], newBenefits[index - 1]];
        return {
          ...prev,
          benefits: newBenefits
        };
      });
    }
  };

  const handleMoveBenefitDown = (index: number) => {
    setFormData(prev => {
      const benefits = prev.benefits || [];
      if (index < benefits.length - 1) {
        const newBenefits = [...benefits];
        [newBenefits[index], newBenefits[index + 1]] = [newBenefits[index + 1], newBenefits[index]];
        return {
          ...prev,
          benefits: newBenefits
        };
      }
      return prev;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name.trim()) {
      setError('Name is required');
      return;
    }
    if (!formData.description.trim()) {
      setError('Description is required');
      return;
    }
    if (formData.maxBranch < 1) {
      setError('Max branches must be at least 1');
      return;
    }
    if (formData.maxTransactionPerMonth < 0) {
      setError('Max transactions per month must be 0 or greater');
      return;
    }
    if (formData.months < 1) {
      setError('Months must be at least 1');
      return;
    }
    if (formData.price < 0) {
      setError('Price must be 0 or greater');
      return;
    }
    if (formData.annuallyDiscountPercent && (formData.annuallyDiscountPercent < 0 || formData.annuallyDiscountPercent > 100)) {
      setError('Annual discount must be between 0 and 100');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (subscription) {
        // Update existing subscription
        await api.updateSubscription(subscription.id, formData);
      } else {
        // Create new subscription
        await api.createSubscription(formData);
      }
      onSubmit();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save subscription');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-medium text-gray-900">
            {subscription ? 'Edit Subscription' : 'Create Subscription'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            disabled={loading}
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                required
                disabled={loading}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Theme Color
              </label>
              <input
                type="color"
                value={formData.themeColor}
                onChange={(e) => handleInputChange('themeColor', e.target.value)}
                className="w-full h-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                disabled={loading}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description *
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              required
              disabled={loading}
            />
          </div>

          {/* Features */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Max Branches *
              </label>
              <input
                type="number"
                min="1"
                value={formData.maxBranch}
                onChange={(e) => handleInputChange('maxBranch', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                required
                disabled={loading}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Max Transactions Per Month *
              </label>
              <input
                type="number"
                min="0"
                value={formData.maxTransactionPerMonth}
                onChange={(e) => handleInputChange('maxTransactionPerMonth', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="0 for unlimited"
                required
                disabled={loading}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Max Transactions Per Day
              </label>
              <input
                type="number"
                min="0"
                value={formData.maxTransactionPerDay}
                onChange={(e) => handleInputChange('maxTransactionPerDay', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="0 for unlimited"
                disabled={loading}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Duration (Months) *
              </label>
              <input
                type="number"
                min="1"
                value={formData.months}
                onChange={(e) => handleInputChange('months', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                required
                disabled={loading}
              />
            </div>
          </div>

          {/* Pricing */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Price (IDR) *
              </label>
              <input
                type="number"
                min="0"
                step="1000"
                value={formData.price}
                onChange={(e) => handleInputChange('price', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                required
                disabled={loading}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Annual Discount (%)
              </label>
              <input
                type="number"
                min="0"
                max="100"
                step="0.01"
                value={formData.annuallyDiscountPercent}
                onChange={(e) => handleInputChange('annuallyDiscountPercent', parseFloat(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="0"
                disabled={loading}
              />
            </div>
          </div>

          {/* Settings */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                F&B Mode *
              </label>
              <select
                value={formData.fnbMode.toString()}
                onChange={(e) => handleInputChange('fnbMode', e.target.value === 'true')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                required
                disabled={loading}
              >
                <option value="true">Enabled</option>
                <option value="false">Disabled</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                External Subscribe Link
              </label>
              <input
                type="url"
                value={formData.externalSubscribeLink}
                onChange={(e) => handleInputChange('externalSubscribeLink', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="https://..."
                disabled={loading}
              />
            </div>
          </div>

          {/* Toggles */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="isPopular"
                checked={formData.isPopular}
                onChange={(e) => handleInputChange('isPopular', e.target.checked)}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                disabled={loading}
              />
              <label htmlFor="isPopular" className="ml-2 block text-sm text-gray-900">
                Mark as Popular
              </label>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="isShow"
                checked={formData.isShow}
                onChange={(e) => handleInputChange('isShow', e.target.checked)}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                disabled={loading}
              />
              <label htmlFor="isShow" className="ml-2 block text-sm text-gray-900">
                Show to Users
              </label>
            </div>
          </div>

          {/* Benefits */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Benefits
            </label>
            <div className="space-y-2">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newBenefit}
                  onChange={(e) => setNewBenefit(e.target.value)}
                  placeholder="Add a benefit..."
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  disabled={loading}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddBenefit();
                    }
                  }}
                />
                <button
                  type="button"
                  onClick={handleAddBenefit}
                  className="px-3 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  disabled={loading || !newBenefit.trim()}
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>
              
              {formData.benefits && formData.benefits.length > 0 && (
                <div className="space-y-1">
                  {formData.benefits.map((benefit, index) => (
                    <div key={index} className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded-md">
                      <div className="flex items-center flex-1">
                        <span className="text-sm text-gray-500 mr-2 font-medium">
                          {index + 1}.
                        </span>
                        <span className="text-sm text-gray-900 flex-1">{benefit}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <button
                          type="button"
                          onClick={() => handleMoveBenefitUp(index)}
                          className="text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                          disabled={loading || index === 0}
                          title="Move up"
                        >
                          <ChevronUp className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleMoveBenefitDown(index)}
                          className="text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                          disabled={loading || index === formData.benefits!.length - 1}
                          title="Move down"
                        >
                          <ChevronDown className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleRemoveBenefit(index)}
                          className="text-red-600 hover:text-red-800 ml-2"
                          disabled={loading}
                          title="Remove benefit"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <div className="flex items-center">
                  <LoadingSpinner size="sm" />
                  <span className="ml-2">Saving...</span>
                </div>
              ) : (
                subscription ? 'Update Subscription' : 'Create Subscription'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SubscriptionForm;
